<template>
  <responsive-svg class="waterfall-chart" padding="20" :paddingLeft="50" v-slot="contentBox">
    <user-space :layout="layout" v-bind="contentBox" v-slot="{xRange, yRange, transform}">
      <transition>
        <rect class="shadow" v-if="focus" v-animated="getShadowGeom(xRange, yRange)"></rect>
      </transition>
      <line-plot
        :data-view="dataView"
        :g-domain="domain.g"
        :x-scale="getXScale(xRange)"
        :y-scale="getYScale(yRange)"
        :curve="curve"
        :classed="g => ({overall: g === '__OVERALL__'})">
      </line-plot>
      <animated-line-plot
        :data-view="dataView.filter(d => highlighted.includes(d.g))"
        :g-domain="domain.g"
        :x-scale="getXScale(xRange)"
        :y-scale="getYScale(yRange)"
        :curve="curve">
      </animated-line-plot>
      <y-axis
        :interval="yInterval"
        :y-scale="getYScale(yRange)"
        anchor="right"
        :formatted="formatY">
      </y-axis>
      <x-axis
        :interval="xInterval"
        :x-scale="getXScale(xRange)"
        :y-scale="getYScale(yRange)"
        anchor="top"
        :formatted="formatX">
      </x-axis>
      <text-label v-bind="getAverageGeom(xRange, yRange, transform)">
        <template v-slot="{x}">
          <tspan dy="1.2em">Average</tspan>&nbsp;
          <tspan :dy="contentBox.width <= 300 && '1.2em'" :x="contentBox.width <= 300 && x">School</tspan>
        </template>
      </text-label>
    </user-space>
  </responsive-svg>
</template>

<script>
import ResponsiveSvg from 'vue-graphics/hocs/ResponsiveSvg'
import UserSpace from 'vue-graphics/hocs/UserSpace'
import LinePlot from 'vue-graphics/components/LinePlot.vue'
import AnimatedLinePlot from 'vue-graphics/components/AnimatedLinePlot.vue'
import XAxis from 'vue-graphics/components/XAxis.vue'
import YAxis from 'vue-graphics/components/YAxis.vue'
import TextLabel from 'vue-graphics/elements/TextLabel'

import Animated from 'vue-graphics/animation/directives/v-animated'
import makeAnimated from 'vue-graphics/animation/makeAnimated'

import {dataViewMixin} from 'vue-graphics/mixins'
import {DomainHelper, IntervalHelper, TransformHelper} from 'vue-graphics/helpers'

import {scalePoint, scaleLinear} from 'd3-scale'

import {curveStepBefore} from 'd3-shape'

export default {
  name: 'WaterfallChart',
  mixins: [dataViewMixin],
  components: {
    ResponsiveSvg,
    UserSpace,
    LinePlot,
    AnimatedLinePlot,
    XAxis,
    YAxis,
    TextLabel: makeAnimated(TextLabel, ['x', 'y'])
  },
  directives: {Animated},
  inheritAttrs: false,
  props: {
    highlighted: {
      type: Array,
      default: () => []
    },
    focus: {
      type: String
    }
  },
  computed: {
    layout () {
      return new TransformHelper().flipY()
    }
  },
  methods: {
    x: d => d.x,
    y: d => d.y,
    g: d => d.g,
    xDomain: DomainHelper.UNIQUE('x'),
    yDomain: () => [0, 1],
    gDomain: DomainHelper.UNIQUE('g'),
    xInterval: IntervalHelper.BETWEEN(),
    yInterval: IntervalHelper.TICKS(4),
    curve: curveStepBefore,
    getXScale (xRange) {
      return scalePoint()
        .domain(this.domain.x)
        .rangeRound(xRange)
        .padding(0)
    },
    getYScale (yRange) {
      return scaleLinear()
        .domain(this.domain.y)
        .rangeRound(yRange)
    },
    getShadowGeom (xRange, yRange) {
      const xScale = this.getXScale(xRange)
      return {
        x: xScale(this.focus) - xScale.step(),
        y: Math.min(yRange[0], yRange[1]),
        width: xScale.step(),
        height: Math.abs(yRange[1] - yRange[0])
      }
    },
    getAverageGeom (xRange, yRange, transform) {
      const x = this.focus || '1'
      const d = this.dataView.find(d => d.g === '__OVERALL__' && d.x === x)
      const xScale = this.getXScale(xRange)
      const yScale = this.getYScale(yRange)
      return {
        x: xScale(d.x) - 0.5 * xScale.step(),
        y: yScale(d.y),
        anchor: 'top',
        postTransform: transform
      }
    },
    formatX: v => 'Phase ' + v[1],
    formatY: v => (v * 100).toFixed() + '%'
  }
}
</script>

<style lang="scss">
.waterfall-chart {
  display: block;
  width: 70%;
  height: 440px;

  @media (max-width: 768px) {
    width: 100%;
    height: 80vw;
    margin-top: 20px;
  }

  text {
    font-family: $font-CuratorRegular;
    font-size: 13px;
    color: #999999;

    @media (max-width: $d_medium - 1) {
      font-size: 13px;
    }
  }

  .vg-line {
    stroke: $greylight;
    fill: none;
    shape-rendering: crispEdges;

    &[data-vg-animated] {
      stroke: $themeColor;
      stroke-width: 2px;
      pointer-events: none;
    }

    &.overall {
      stroke: #919191;
      stroke-width: 1px;
      stroke-dasharray: 4px;
      pointer-events: none;
    }
  }

  .vg-baseline,
  .vg-tick-mark {
    display: none;
  }

  .vg-y-axis .vg-tick:first-child {
    display: none;
  }

  .shadow {
    fill: $shadowColor;

    &.v-enter-active,
    &.v-leave-active {
      transition: opacity 0.66667s;
    }

    &.v-enter,
    &.v-leave-to {
      opacity: 0;
    }
  }
}
</style>
